import { Button, Input, ToastMessage } from '@cokitchen/cokitchen-components';
import { RmxArrowBackIcon } from 'cokitchen-icons';
import React, { useEffect, useState } from 'react';
import useProductionReportContext from 'contexts/kitchen/production-report.context/context';
import { apiService } from 'services/base.service';
import { STORAGE_KEYS } from 'utils/constants';
import ActivityIndicator from 'components/base/loader/activityIndicator';
import { toast } from 'react-toastify';

type MealItem = {
  id: string;
  name: string;
  portion: string;
  show: boolean;
};

const UpdateMeals = () => {
  const { setIsBackLink, setBackLinkTitle } = useProductionReportContext();
  const [formData, setFormData] = useState<MealItem[]>([]);
  const [isFetching, setIsFetching] = useState(true);
  const cokitchenId = localStorage.getItem(STORAGE_KEYS.COKITCHEN);

  const fetchMeals = async () => {
    if (!cokitchenId) return;
    try {
      setIsFetching(true);
      const response = await apiService.get('cokitchen-item', {
        params: {
          cokitchen_id: cokitchenId
        }
      });
      if (response?.data?.data) {
        setFormData(
          response.data.data.map((meal: any) => ({
            id: meal.id,
            name: meal.name || meal.kitchen_item?.name || '',
            portion: '',
            show: false
          }))
        );
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching meals'}
          type='error'
        />
      );
    } finally {
      setIsFetching(false);
    }
  };

  const showForm = (id: string) =>
    setFormData(formData.map((meal) => (meal.id === id ? { ...meal, show: !meal.show } : meal)));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, i: number) => {
    const { name, value } = e.target;
    setFormData(formData.map((meal, index) => (index === i ? { ...meal, [name]: value } : meal)));
  };

  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle('Return to previous');
    fetchMeals();
    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);

  if (isFetching) {
    return (
      <div className='flex h-[calc(100vh-200px)] items-center justify-center'>
        <ActivityIndicator />
      </div>
    );
  }

  return (
    <div className='mx-auto max-w-lg'>
      <h2 className='font-semibold text-2xl text-[#2E3031] mb-10'>Update meals</h2>
      <div className='bg-[#F7F7F8] w-full h-auto p-1 rounded-md'>
        <div className='bg-white w-full h-auto rounded-md'>
          <div className='pb-10'>
            <h4 className='font-semibold text-xl text-[#2E3031] pt-6 px-4 md:px-7 pb-8'>
              Update portions produced for meals
            </h4>
            {formData.length === 0 ? (
              <p className='px-4 md:px-7 text-[#6F7376]'>No meals found</p>
            ) : (
              formData.map((meal, i) => (
                <div key={meal.id}>
                  <div
                    className={`flex items-center justify-between py-[14px] px-4 md:px-7 bg-[#F7F7F7] ${
                      !meal.show && i < formData.length - 1 ? 'border-b border-[#EDEDEE]' : ''
                    }`}>
                    <p className='font-medium text-[#2E3031] text-base'>{meal.name}</p>
                    <button
                      className='font-medium text-xs text-[#5E6164] py-2 px-6 bg-[#FFFFFF] rounded-full border border-[#EAEBEB]'
                      onClick={() => showForm(meal.id)}>
                      {meal.show ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <div className={`${meal.show ? 'px-4 md:px-7 py-6 h-[126px]' : 'h-0 hidden'}`}>
                    <Input
                      label='Portion produced'
                      placeholder='0'
                      value={formData[i].portion}
                      name='portion'
                      design='bordered'
                      onChange={(e) => handleChange(e, i)}
                    />
                  </div>
                </div>
              ))
            )}
          </div>

          <div className='flex gap-6 py-7 px-4 md:px-7 items-center justify-between border-t-4 border-t-[#F7F7F8] w-full flex-wrap-reverse'>
            <div className='flex items-center gap-3 cursor-pointer'>
              <div className='flex w-[30px] h-[30px] bg-[#F4F5F5] rounded-full justify-center items-center'>
                <RmxArrowBackIcon className='text-gray-9' />
              </div>
              <p className='text-sm text-gray-10'>Back</p>
            </div>

            <div className='flex gap-6 items-center justify-end ml-auto flex-wrap'>
              <Button variant='link' color='gray' small>
                Cancel
              </Button>
              <Button variant='block' color='primary' small className='w-[153px] h-[43px]'>
                Confirm
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateMeals;
